// React imports
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Libraries
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// Hooks
import { useRecentDealsSelector } from './useRecentDealsSelector';
import { useRecentDealsDealershipInfo } from './useRecentDealsDealershipInfo';

// Store/Actions
import { selectDeal } from '../store/actionCreators';

// Utils
import { formatLastUpdated } from '../utils/formatter';
import { getRecentDealDisplayName } from '../utils/getRecentDealDisplayName';
import { isQuoteDeal } from '../utils/isQuoteDeal';

/**
 * Custom React hook to provide a memoized list of recent deals with formatted fields and selection logic.
 *
 * - Maps and formats each deal with trade-in label and last activity summary.
 * - Ensures at least one deal is selected if none are marked as selected.
 * - Returns the modified deals array and a flag for open deal enablement.
 *
 * @returns {{ modifiedDeals: Array, hasOpenDealEnabled: boolean }}
 */
import { getTradeInLabel } from '../utils/formatter';

export const useMemoizedRecentDeals = () => {
    const dispatch = useDispatch();
    const { deals } = useRecentDealsSelector();
    const isQuoteModeEnabled = useSelector(featureToggleSelectors.isManagerViewQuoteModeEnabled);
    const { getDealerInfo } = useRecentDealsDealershipInfo();
    // The list is fetched scoped to a single dealership (mmdId), so that mmdId is the dealer id used
    // in the Quote Mode name (Q-{dealerId}-...) for every customer-less row in the list.
    const dealerId = getDealerInfo()?.mmdId;

    const onDealSelect = useCallback(
        (dealId: string) => {
            dispatch(selectDeal(dealId));
        },
        [dispatch]
    );

    const memoizedRecentDeals = useMemo(() => {
        if (!deals || deals.length === 0) {
            return { modifiedDeals: [], hasOpenDealEnabled: false };
        }

        // TA4147186: with Quote Mode off there is no way to view or work a quote, so customer-less records must
        // not reach the list at all — otherwise they render as blank-name rows that can still be opened.
        const visibleDeals = isQuoteModeEnabled ? deals : deals.filter((deal) => !isQuoteDeal(deal));

        if (visibleDeals.length === 0) {
            return { modifiedDeals: [], hasOpenDealEnabled: false };
        }

        const modifiedDeals = visibleDeals.map((deal) => {
            const tradeInLabel = getTradeInLabel(deal.tradeInVehicle);
            const lastActivitySummary = formatLastUpdated(deal.lastUpdatedDate, deal.changedbyApp, '');
            const className = deal.isSelected ? 'active' : '';
            const displayName = getRecentDealDisplayName(deal, { dealerId, quoteNamingEnabled: isQuoteModeEnabled });

            return {
                ...deal,
                tradeInLabel,
                lastActivitySummary,
                displayName,
                onDealSelect,
                className
            };
        });

        const selectedDeals = modifiedDeals.filter((deal) => deal.isSelected);
        if (!selectedDeals.length && modifiedDeals.length) {
            modifiedDeals[0].isSelected = true;
            modifiedDeals[0].className = 'active';
        }

        return { modifiedDeals, hasOpenDealEnabled: true };
    }, [deals, onDealSelect, dealerId, isQuoteModeEnabled]);

    return memoizedRecentDeals;
};
